import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';

//  --- Widgets ---
import EventDetailModal from './EventDetailModal';
import AppointmentFormModal from './AppointmentFormModal';

// -------------------
//  --- Context & Hooks ---
import { useUserAgenda } from '../../../../API/Hooks/useUserAgenda';
import { useUser } from '../../../../API/Context/UserContext';
import { useLanguage } from '../../../../API/Context/LanguageContext';
import { useUserMeBusinessHours } from '../../../../API/Hooks/useUserMeBusinessHours';
import { useEarliestAgendaSpace } from '../../../../API/Hooks/useUserEarliestAgenda';

// -------------------
//  --- Utils ---
import { DateTime } from 'luxon';
import { lan_number_to_language } from '../../../../Utils/languageUtils';
import { getWeekAndYear } from '../../../../Utils/dateUtils';
import { transformEvents } from '../../../../Utils/calendarUtils';

// -------------------
//  --- Config  ---
import { agendaValues } from '../../../../ConfigFiles/values/agenda_values';
import { dateFormats } from '../../../../ConfigFiles/date_formats';
import { buttonTextMap } from '../../../../ConfigFiles/multilingual/VocabAppointments';
import './AgendaWidget.css';

const CALENDAR_HEIGHT = '80vh'; 

const getLocalTimezoneDisplayName = () => {
    const dt = DateTime.local();
    return dt.toFormat('z ZZZZ'); 
};

/**
 * Converts minutes since midnight (e.g., 540) to FullCalendar time string ("09:00:00")
 */
const minutesToTimeString = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:00`;
};

/**
 * Transforms office_hours dict format into FullCalendar's businessHours format.
 * Dict Keys: "0" (Monday) to "6" (Sunday)
 * Dict Values: Array of { start: number (minutes), end: number (minutes) }
 * FC format: [{ daysOfWeek: [1], startTime: '09:00', endTime: '17:00' }, ...]
 */
const transformBusinessHours = (officeHours) => {
    if (!officeHours || typeof officeHours !== 'object') return [];

    const fcHours = [];

    // Map keys "0"-"6" (Mon-Sun) to FullCalendar's day indices (Mon=1, ..., Sat=6, Sun=0)
    const dayMap = {
        '0': 1, // Monday
        '1': 2, // Tuesday
        '2': 3, // Wednesday
        '3': 4, // Thursday
        '4': 5, // Friday
        '5': 6, // Saturday
        '6': 0, // Sunday
    };

    Object.entries(officeHours).forEach(([dayKey, intervals]) => {
        const fcDayOfWeek = dayMap[dayKey];
        if (fcDayOfWeek === undefined || !Array.isArray(intervals)) return;

        intervals.forEach((interval) => {
            if (typeof interval.start === 'number' && typeof interval.end === 'number') {
                fcHours.push({
                    daysOfWeek: [fcDayOfWeek],
                    startTime: minutesToTimeString(interval.start),
                    endTime: minutesToTimeString(interval.end),
                });
            }
        });
    });

    return fcHours;
};

const AgendaWidget = () => {
  //  --- Context & Hooks ---
  const { lan } = useLanguage();
  const { user } = useUser();
  const { agenda, isLoading, error, fetchWeeklySchedule } = useUserAgenda();
  const { officeHours } = useUserMeBusinessHours();
  const { earliestSpace } = useEarliestAgendaSpace();

  // Transform business hours reactively whenever user officeHours change in UserContext
  const businessHours = useMemo(() => {
      return transformBusinessHours(officeHours);
  }, [officeHours]);

  //  Function to navigate to earliest available slot
  const goToEarliest = useCallback(() => {
    if (earliestSpace?.space && calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.gotoDate(earliestSpace.space);
    }
  }, [earliestSpace]);

  // Dot header indicator
  const renderDayHeader = (headerProps) => {
    const defaultText = headerProps.text;
    const isWeekOrDayView = headerProps.view.type.includes('timeGrid');

    if (!earliestSpace || !isWeekOrDayView) return defaultText;

    const headerDate = DateTime.fromJSDate(headerProps.date).toISODate();
    const targetDate = DateTime.fromJSDate(earliestSpace.space).toISODate();

    if (headerDate === targetDate) {
      return (
        <div className="custom-header-container">
          <span className="header-text">{defaultText}</span>
          <span className="earliest-availability-dot header-dot" title="Próxima disponibilidad"></span>
        </div>
      );
    }
    return defaultText;
  };

  const renderDayCell = (dayProps) => {
    const defaultNumber = dayProps.dayNumberText;
    const isMonthView = dayProps.view.type === 'dayGridMonth';

    if (!earliestSpace || !isMonthView) return defaultNumber;

    const cellDate = DateTime.fromJSDate(dayProps.date).toISODate();
    const targetDate = DateTime.fromJSDate(earliestSpace.space).toISODate();

    if (cellDate === targetDate) {
      return (
        <div className="custom-cell-container">
          <div className="fc-daygrid-day-number">{defaultNumber}</div>
          <span className="earliest-availability-dot cell-dot" title="Próxima disponibilidad"></span>
        </div>
      );
    }
    return defaultNumber;
  };

  const localizedButtonText = buttonTextMap[lan] || buttonTextMap[1];
  const calendarRef = useRef(null);
  
  // State to track current view
  const [currentView, setCurrentView] = useState('timeGridWeek');

  //  Modal State
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isCreationModalOpen, setIsCreationModalOpen] = useState(false);
  const [clickedEventData, setClickedEventData] = useState(null);
  const [selectedDates, setSelectedDates] = useState(null); 

  // Derived calendar events
  const calendarEvents = transformEvents(agenda);

  // --- CRUD Success Handler ---
  const onCrudSuccess = (transformedEvent) => { 
      if (!calendarRef.current || !transformedEvent) {
          handleCloseDetailModal(); 
          handleCloseCreationModal(); 
          return;
      }

      const calendarApi = calendarRef.current.getApi();
      
      const oldEvent = calendarApi.getEventById(transformedEvent.id);
      if (oldEvent) {
          oldEvent.remove();
      }
      
      calendarApi.addEvent(transformedEvent); 

      handleCloseDetailModal(); 
      handleCloseCreationModal();

      const currentViewDates = {
          start: calendarApi.view.currentStart 
      };
      handleDatesSet(currentViewDates);
  };

  // --- Date Range Change Handler ---
const handleDatesSet = (info) => {
    setCurrentView(info.view.type);

    const { week_number, year } = getWeekAndYear(info.start);
    if (user?.username) {
      // Note the signature order: (username, year, week_number)
      fetchWeeklySchedule(user.username, year, week_number);
    }
};

  // --- Modal Handlers ---
  const handleEventClick = (clickInfo) => {
    setClickedEventData({
        fullCalendarEvent: clickInfo.event, 
        apiEventData: clickInfo.event.extendedProps
    });
    setIsDetailModalOpen(true);
  };

  const handleCloseDetailModal = () => {
    setIsDetailModalOpen(false);
    setClickedEventData(null);
  };

  const handleDateSelect = (selectInfo) => {
    setSelectedDates({
        start: selectInfo.start,
        end: selectInfo.end,
    });
    setIsCreationModalOpen(true);
    selectInfo.view.calendar.unselect(); 
  };

  const handleDateClick = (clickInfo) => {
    const calendarApi = calendarRef.current.getApi();
    
    if (calendarApi.view.type === 'dayGridMonth') {
      calendarApi.changeView('timeGridWeek', clickInfo.dateStr);
    }
  };

  const handleCloseCreationModal = () => {
    setIsCreationModalOpen(false);
    setSelectedDates(null);
  };

  const renderEventContent = (eventInfo) => {
    const startDt = DateTime.fromJSDate(eventInfo.event.start);
    const endDt = DateTime.fromJSDate(eventInfo.event.end);
    const startTime = startDt.toLocaleString(DateTime.TIME_SIMPLE);
    const endTime = endDt.toLocaleString(DateTime.TIME_SIMPLE);
    
    const attendeeName = eventInfo.event.extendedProps.displayAttendee || eventInfo.event.title;

    return (
      <div className="custom-event-content">
        <div className="event-title-line">
          <strong>{attendeeName}</strong>
        </div>
        <div className="event-time-line">
          {startTime} - {endTime}
        </div>
      </div>
    );
  };

  if (error) {
    return <div className="error-message">Error: {error}</div>;
  }

  const isTimeSlotView = currentView === 'timeGridWeek' || currentView === 'timeGridDay';

  return (
    <div className="agenda-widget-container">
      {isLoading && <div className="loading-overlay">Loading Agenda...</div>}
      
      <FullCalendar
        firstDay={1}
        locale={lan_number_to_language(lan)}
        buttonText={localizedButtonText}
        ref={calendarRef}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        
        initialView="timeGridWeek"
        events={calendarEvents} 
        timeZone='local'
        
        customButtons={{
          nextAvailable: {
            text: 'Próxima cita',
            click: goToEarliest,
          }
        }}

        headerToolbar={{
          left: 'title',
          center: 'nextAvailable',
          right: 'prev,next,dayGridMonth,timeGridWeek,today'
        }}
        
        dayCellContent={renderDayCell}
        dayHeaderContent={renderDayHeader}
        
        datesSet={handleDatesSet} 
        
        eventContent={renderEventContent}
        eventClick={handleEventClick}
        dateClick={handleDateClick}
        
        // Pass transformed business hours directly
        businessHours={businessHours} 
        
        allDaySlot={false}
        slotMinTime={agendaValues.slotMinTime}
        slotMaxTime={agendaValues.slotMaxTime}
        
        height={CALENDAR_HEIGHT} 
        
        nowIndicator={true} 
        selectable={isTimeSlotView}
        select={handleDateSelect}
      />
      
      {/* --- Modals --- */}
      <EventDetailModal 
        isOpen={isDetailModalOpen}
        onClose={handleCloseDetailModal}
        eventData={clickedEventData}
        onSuccess={onCrudSuccess}
      />
      <AppointmentFormModal
        isOpen={isCreationModalOpen}
        onClose={handleCloseCreationModal}
        initialDates={selectedDates} 
        onSuccess={onCrudSuccess} 
      />
    </div>
  );
};

export default AgendaWidget;
